import { type ComponentProps, type ReactNode, useMemo } from 'react';

import TrendBadge from '@/components/Badge/TrendBadge';
import { TooltipWrapper } from '@/components/Tooltip/TooltipWrapper';
import { Text } from '@/components/Typography/Text';

import { StatCardTable, StatCardTableHorizBar, StatCardTableRow } from '..';

import { Highlight, StatCardBase } from './StatCardBase';

type Count = {
  label: ReactNode | string;
  value: number;
  changes?: number;
};

type Props = {
  data: Count[];
  highlight: {
    prefix?: string;
    label: string | number;
    suffix?: string;
  };
} & Omit<ComponentProps<typeof StatCardBase>, 'highlight'>;

/**
 * Overviewで表示する統計情報のうち、集計情報を表示するためのカードです。
 */
export function StatCardCount(props: Props) {
  const { data, highlight, ...rest } = props;

  const maxValue = useMemo(
    () => data.reduce((max, count) => Math.max(max, count.value), 0),
    [data],
  );

  const HighlightComponent = (
    <Highlight
      prefix={highlight?.prefix}
      label={highlight.label}
      suffix={highlight?.suffix}
    />
  );

  return (
    <StatCardBase {...rest} highlight={HighlightComponent}>
      <StatCardTable>
        {data.map((count, index) => (
          <CountRow key={index} {...count} maxValue={maxValue} />
        ))}
      </StatCardTable>
    </StatCardBase>
  );
}

function CountRow({
  label,
  value,
  changes,
  maxValue,
}: Count & { maxValue: number }) {
  return (
    <StatCardTableRow>
      {/* コレぐらいのサボり感があったほうが使い勝手がいいはず */}
      {typeof label === 'string' ? (
        <TooltipWrapper content={label} ellipsisOnly>
          <Text
            variant="pSmall"
            className="line-clamp-1 w-20 break-all capitalize"
          >
            {label}
          </Text>
        </TooltipWrapper>
      ) : (
        label
      )}
      <StatCardTableHorizBar value={value} maxValue={maxValue} />
      {changes !== undefined && (
        <>
          {changes === 0 ? (
            <Text variant="pSmall" className="text-secondary-on-neutral-fill">
              –
            </Text>
          ) : (
            <TrendBadge value={changes} variant="number" color="neutral" />
          )}
        </>
      )}
    </StatCardTableRow>
  );
}
